/**
 * Simulation Store
 *
 * Replaces the 110 useState hooks in ConflictCoach.js.
 * Manages the full simulation lifecycle: scenario selection → active turns → completion.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  Scenario,
  Turn,
  WorldState,
  ScenarioFramework,
  ScenarioLocation,
  ScenarioCategory,
  DifficultyLevel,
  SimulationType,
  DispatchResource,
} from '@copapp/core/types';

// ---------------------------------------------------------------------------
// Simulation Phase State Machine
// ---------------------------------------------------------------------------

export type SimulationPhase =
  | 'idle'
  | 'scenario-select'
  | 'framework-config'
  | 'generating-scenario'
  | 'ready'
  | 'active'
  | 'processing-turn'
  | 'paused'
  | 'ending'
  | 'completed'
  | 'travesty'
  | 'debrief';

const VALID_TRANSITIONS: Record<SimulationPhase, SimulationPhase[]> = {
  'idle': ['scenario-select'],
  'scenario-select': ['framework-config', 'generating-scenario', 'idle'],
  'framework-config': ['generating-scenario', 'scenario-select', 'idle'],
  'generating-scenario': ['ready', 'scenario-select'],
  'ready': ['active', 'idle'],
  'active': ['processing-turn', 'paused', 'ending', 'travesty'],
  'processing-turn': ['active', 'travesty', 'ending'],
  'paused': ['active', 'ending', 'idle'],
  'ending': ['completed'],
  'completed': ['debrief', 'idle'],
  'travesty': ['debrief', 'idle'],
  'debrief': ['idle', 'scenario-select'],
};

// ---------------------------------------------------------------------------
// Store State
// ---------------------------------------------------------------------------

interface SimulationState {
  // Phase
  phase: SimulationPhase;

  // Session
  sessionId: string | null;
  officerUid: string | null;
  departmentId: string | null;

  // Scenario config
  scenarioCategory: ScenarioCategory | null;
  framework: ScenarioFramework | null;
  location: ScenarioLocation | null;
  difficulty: DifficultyLevel;
  simulationType: SimulationType;
  customInput: string;

  // Active simulation
  scenario: Scenario | null;
  turns: Turn[];
  worldState: WorldState | null;
  currentTurnNumber: number;

  // Time tracking
  simulatedTimeSeconds: number;
  realTimeStartMs: number | null;
  realTimeAccumulatedMs: number;

  // Dispatch
  dispatchResources: DispatchResource[];

  // Status
  isTravestySession: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Store Actions
// ---------------------------------------------------------------------------

interface SimulationActions {
  // Phase transitions
  setPhase: (phase: SimulationPhase) => void;

  // Setup
  startScenarioSelect: () => void;
  setScenarioCategory: (category: ScenarioCategory) => void;
  setFramework: (framework: ScenarioFramework) => void;
  setLocation: (location: ScenarioLocation | null) => void;
  setDifficulty: (difficulty: DifficultyLevel) => void;
  setSimulationType: (type: SimulationType) => void;
  setCustomInput: (input: string) => void;

  // Session lifecycle
  initSession: (params: {
    sessionId: string;
    officerUid: string;
    departmentId: string | null;
    scenario: Scenario;
    worldState: WorldState;
    turns?: Turn[];
    currentTurnNumber?: number;
    simulatedTimeSeconds?: number;
    realTimeAccumulatedMs?: number;
  }) => void;

  // Turn management
  startTurn: () => void;
  completeTurn: (turn: Turn) => void;

  // World state
  updateWorldState: (worldState: WorldState) => void;
  addDispatchResource: (resource: DispatchResource) => void;
  updateDispatchResource: (type: string, update: Partial<DispatchResource>) => void;

  // Time
  startRealTimer: () => void;
  pauseRealTimer: () => void;
  addSimulatedTime: (seconds: number) => void;

  // Session end
  endSession: () => void;
  triggerTravesty: () => void;

  // Error
  setError: (error: string | null) => void;

  // Reset
  resetSimulation: () => void;
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState: SimulationState = {
  phase: 'idle',
  sessionId: null,
  officerUid: null,
  departmentId: null,
  scenarioCategory: null,
  framework: null,
  location: null,
  difficulty: 'moderate',
  simulationType: 'text',
  customInput: '',
  scenario: null,
  turns: [],
  worldState: null,
  currentTurnNumber: 0,
  simulatedTimeSeconds: 0,
  realTimeStartMs: null,
  realTimeAccumulatedMs: 0,
  dispatchResources: [],
  isTravestySession: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSimulationStore = create<SimulationState & SimulationActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setPhase: (phase) => {
          const current = get().phase;
          const valid = VALID_TRANSITIONS[current];
          if (!valid?.includes(phase)) {
            console.warn(`Invalid phase transition: ${current} → ${phase}`);
            return;
          }
          set({ phase }, false, `phase/${phase}`);
        },

        startScenarioSelect: () => {
          const state = get();
          if (state.phase !== 'idle' && state.phase !== 'debrief') return;
          set({ ...initialState, phase: 'scenario-select' }, false, 'startScenarioSelect');
        },

        setScenarioCategory: (category) => set({ scenarioCategory: category }, false, 'setCategory'),
        setFramework: (framework) => set({ framework }, false, 'setFramework'),
        setLocation: (location) => set({ location }, false, 'setLocation'),
        setDifficulty: (difficulty) => set({ difficulty }, false, 'setDifficulty'),
        setSimulationType: (type) => set({ simulationType: type }, false, 'setSimType'),
        setCustomInput: (input) => set({ customInput: input }, false, 'setCustomInput'),

        initSession: ({ sessionId, officerUid, departmentId, scenario, worldState, turns, currentTurnNumber, simulatedTimeSeconds, realTimeAccumulatedMs }) => {
          set({
            sessionId,
            officerUid,
            departmentId,
            scenario,
            worldState,
            turns: turns || [],
            currentTurnNumber: currentTurnNumber || 0,
            simulatedTimeSeconds: simulatedTimeSeconds || 0,
            realTimeAccumulatedMs: realTimeAccumulatedMs || 0,
            isTravestySession: false,
            error: null,
            phase: 'ready',
          }, false, 'initSession');
        },

        startTurn: () => {
          const state = get();
          if (state.phase !== 'active') return;
          set({ phase: 'processing-turn' }, false, 'startTurn');
        },

        completeTurn: (turn) => {
          set((state) => ({
            turns: [...state.turns, turn],
            currentTurnNumber: turn.turnNumber,
            simulatedTimeSeconds: state.simulatedTimeSeconds + turn.simulatedTimeSeconds,
            phase: 'active',
            error: null,
          }), false, 'completeTurn');
        },

        updateWorldState: (worldState) => set({ worldState }, false, 'updateWorldState'),

        addDispatchResource: (resource) => {
          set((state) => ({
            dispatchResources: [...state.dispatchResources, resource],
          }), false, 'addDispatch');
        },

        updateDispatchResource: (type, update) => {
          set((state) => ({
            dispatchResources: state.dispatchResources.map((r) =>
              r.type === type ? { ...r, ...update } : r,
            ),
          }), false, 'updateDispatch');
        },

        startRealTimer: () => set({ realTimeStartMs: Date.now() }, false, 'startTimer'),

        pauseRealTimer: () => {
          const state = get();
          if (state.realTimeStartMs) {
            set({
              realTimeAccumulatedMs: state.realTimeAccumulatedMs + (Date.now() - state.realTimeStartMs),
              realTimeStartMs: null,
            }, false, 'pauseTimer');
          }
        },

        addSimulatedTime: (seconds) => {
          set((state) => ({
            simulatedTimeSeconds: state.simulatedTimeSeconds + seconds,
          }), false, 'addSimTime');
        },

        endSession: () => {
          get().pauseRealTimer();
          set({ phase: 'ending' }, false, 'endSession');
        },

        triggerTravesty: () => {
          get().pauseRealTimer();
          set({ phase: 'travesty', isTravestySession: true }, false, 'travesty');
        },

        setError: (error) => set({ error }, false, 'setError'),

        resetSimulation: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'copapp-simulation',
        partialize: (state) => ({
          // officerUid is the OWNERSHIP tag: recovery is only offered to the
          // account that started the session (AuthContext enforces it).
          officerUid: state.officerUid,
          sessionId: state.sessionId,
          phase: state.phase,
          turns: state.turns,
          worldState: state.worldState,
          scenario: state.scenario,
          currentTurnNumber: state.currentTurnNumber,
          simulatedTimeSeconds: state.simulatedTimeSeconds,
          realTimeAccumulatedMs: state.realTimeAccumulatedMs,
        }),
        // A tab killed mid-turn rehydrates a transient phase no UI path can
        // leave (the processing spinner forever). In-flight work never
        // survives a reload, so land back on a state that has doors. merge()
        // runs during hydration — before any subscriber sees the state.
        merge: (persisted, current) => {
          const merged = { ...current, ...(persisted as Partial<SimulationState>) };
          // 'ending' means the officer already chose to end — the session's
          // life is over. Resurrecting it as 'active' hands back a dead call
          // whose turns all fail server-side. Restore nothing.
          if (merged.phase === 'ending') {
            return { ...current };
          }
          if (merged.phase === 'processing-turn') {
            merged.phase = merged.sessionId ? 'active' : 'idle';
          }
          if (merged.phase === 'generating-scenario' && !merged.sessionId) {
            merged.phase = 'scenario-select';
          }
          return merged;
        },
      },
    ),
    { name: 'SimulationStore' },
  ),
);
